// Demo-mode configuration: limits, seed data builders, fake email addresses.
//
// Limits are soft — when crossed, the demo store evicts the oldest item FIFO
// and surfaces a toast. Goal is "never feels broken" while protecting the
// floating inbox from being overwhelmed.

import type { School, Lesson, Feedback, Period, ClassEntity, PupilEntity, PillarEntry, PillarSuggestion, ClassContinuityPillars } from '@/lib/app-store';
import { BOARD_DO_NOW_IMAGE_URL, BOARD_DO_NOW_IMAGE_NAME, RIVER_DIAGRAM_IMAGE_URL, RIVER_DIAGRAM_IMAGE_NAME, RIVER_DIAGRAM_IMAGE_BYTES } from './sample-media';
import type { StaffMember } from '@/lib/onboarding-store';

/**
 * Bump whenever the seeded demo CONTENT changes: lessons, classes, school,
 * policies, pupils.
 *
 * Without this, seeding only ran when `cmc_app_state` was missing, so anyone
 * with a demo session already open kept whatever was seeded the first time.
 * Editing a lesson and reloading changed nothing, which is a slow way to
 * discover that the thing you are looking at is months old.
 */
export const DEMO_SEED_VERSION = '2026-09-16-supplied-images';

export const DEMO_LIMITS = {
  lessons: 5,
  staff: 3,
  secondaryContacts: 3,
  feedback: 10,
  approvals: 5,
  inboxItems: 50, // hard cap on stored inbox — FIFO eviction
};

// Fake addresses used to auto-fill greyed-out email inputs. Choose by role.
export const DEMO_EMAILS = {
  schoolAdmin: 'test-admin@example.com',
  teacher: (n = 1) => `test-teacher-${n}@example.com`,
  cover: (n = 1) => `test-cover-${n}@example.com`,
  secondary: (n = 1) => `test-contact-${n}@example.com`,
  generic: 'test@example.com',
} as const;

// One-of-everything seed so the demo dashboard isn't barren on first entry.
// The user can add more, up to DEMO_LIMITS.
export function buildSeedSchool(): School {
  const periods: Period[] = [
    { number: '1', startTime: '08:30', endTime: '09:30' },
    { number: '2', startTime: '09:30', endTime: '10:30' },
    { number: '3', startTime: '10:45', endTime: '11:45' },
    { number: '4', startTime: '11:45', endTime: '12:45' },
    { number: '5', startTime: '13:30', endTime: '14:30' },
    { number: '6', startTime: '14:30', endTime: '15:30' },
  ];
  const seedStaff: StaffMember[] = [
    {
      id: 'demo-staff-1',
      firstName: 'Marcus',
      lastName: 'Allen',
      email: DEMO_EMAILS.teacher(1),
      department: 'Mathematics',
      subjects: ['Mathematics'],
      inviteCode: 'DEMO1234',
      inviteSent: true,
      status: 'activated',
      invitedAt: Date.now() - 1000 * 60 * 60 * 24 * 7,
      activatedAt: Date.now() - 1000 * 60 * 60 * 24 * 5,
    },
    {
      id: 'demo-staff-2',
      firstName: 'Priya',
      lastName: 'Shah',
      email: DEMO_EMAILS.teacher(2),
      department: 'Science',
      subjects: ['Science'],
      inviteCode: 'DEMO5678',
      inviteSent: true,
      status: 'activated',
      invitedAt: Date.now() - 1000 * 60 * 60 * 24 * 7,
      activatedAt: Date.now() - 1000 * 60 * 60 * 24 * 5,
    },
  ];
  return {
    name: 'Bromley Demo Academy',
    logo: '🏫',
    logoImage: null,
    primary: '#1e3a5f',
    urn: '999999',
    dfeNumber: '999/9999',
    address: 'Bromley',
    postcode: 'BR2 9JE',
    staff: seedStaff,
    subjects: ['English', 'Mathematics', 'Science', 'History', 'Geography', 'Art', 'PE'],
    rooms: ['M1', 'M2', 'S1', 'Hall', 'Lib'],
    yearGroups: ['Year 7', 'Year 8', 'Year 9', 'Year 10', 'Year 11'],
    periods,
    contacts: {
      adminEmail: DEMO_EMAILS.schoolAdmin,
      headTeacher: {
        name: 'Sarah Patel',
        email: DEMO_EMAILS.schoolAdmin,
        phone: '02012345678',
      },
      secondaryContacts: [
        {
          name: 'Jane Smith',
          email: DEMO_EMAILS.secondary(1),
          role: 'SLT',
          position: 'SLT',
          phone: '',
        },
      ],
    },
    // The classroom computer login a cover teacher is handed. Without it the
    // cover brief renders Key School Rules and Emergency Contacts and nothing
    // else in that row, and the demo quietly under-sells one of the three
    // things it is there to show.
    guestAccount: {
      username: 'cover.guest',
      password: 'Welcome2026!',
      notes: 'Logs on to any classroom computer and opens the shared drive. It has no email, so nothing you do on it goes anywhere.',
    },
    policies: {
      behaviourPolicy: {
        fileName: 'Bromley-Demo-Academy-Behaviour-Policy-2026.pdf',
        keyPoints:
          'Three-step warning system applied consistently in every classroom. Phones off and in bags. One pupil out of the room at a time, carrying a corridor pass. Detentions are set by the class teacher, not by cover staff.',
        extractedPoints: [
          'Staff follow the three-step warning system: verbal warning, formal warning recorded, then removal to the partner classroom. (Behaviour Policy, 3.1)',
          'Phones seen or heard during lessons are confiscated to the front desk until the end of the school day. (Behaviour Policy, 6.2)',
          'Only one pupil may be out of the classroom at any time, carrying a corridor pass. (Behaviour Policy, 4.4)',
          'Detentions are issued by the class teacher. Cover staff log the incident instead. (Cover Policy, 2.3)',
          'Any safeguarding disclosure goes to the Designated Safeguarding Lead the same day. Never promise a pupil confidentiality. (Safeguarding Policy, 1.2)',
        ],
      },
    },
    departments: ['Mathematics', 'Science', 'English', 'Humanities'],
    // What a cover teacher is shown as "Key School Rules". Every line has to be
    // traceable to a policy clause, because that is the claim the panel makes.
    // These used to be generic good manners ("Greet the class warmly and take a
    // register"), which no policy says and which taught a demo audience that
    // the panel was advice rather than the school's own written rules. Each one
    // now carries the document and clause it came from.
    coverTeacherRules: [
      'Follow the three-step warning system: verbal warning, formal warning recorded on the board, then removal to the partner classroom. (Behaviour Policy, 3.1)',
      'Phones must be off and in bags. Anything seen or heard is confiscated to the front desk until the end of the day. (Behaviour Policy, 6.2)',
      'One pupil out of the room at a time, and only with a corridor pass. (Behaviour Policy, 4.4)',
      'Do not set detentions. Log the incident for the class teacher, who issues any sanction. (Cover Policy, 2.3)',
      'Report any safeguarding concern to the Designated Safeguarding Lead the same day. (Safeguarding Policy, 1.2)',
    ],
  };
}

export function buildSeedLesson(school: School): Lesson {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const dateISO = tomorrow.toISOString().split('T')[0];
  return {
    id: 'demo-lesson-1',
    status: 'awaiting_approval',
    createdAt: Date.now() - 1000 * 60 * 60 * 4,
    dateISO,
    classroom: school.rooms[0] ?? 'M1',
    subject: school.subjects[1] ?? 'Mathematics',
    yearGroup: school.yearGroups[2] ?? 'Year 9',
    unit: 'Algebra basics',
    periodNumber: '2',
    periodTime: '09:30 - 10:30',
    coveringFor: `${school.staff[0]?.firstName ?? 'Marcus'} ${school.staff[0]?.lastName ?? 'Allen'}`,
    lessonAim: 'Students will solve simple linear equations.',
    objectives: [
      'Identify the unknown variable',
      'Apply inverse operations to isolate the variable',
      'Check answers by substitution',
    ],
    steps: [
      { id: 'step-1', minutes: 10, title: 'Starter', detail: 'Mental maths warm-up on the board.' },
      { id: 'step-2', minutes: 25, title: 'Main', detail: 'Work through worksheet Q1–Q8 in pairs.' },
      { id: 'step-3', minutes: 15, title: 'Review', detail: 'Class discussion of answers.' },
      { id: 'step-4', minutes: 10, title: 'Plenary', detail: 'Exit ticket: one question on the board.' },
    ],
    resources: [],
    resourcePaths: [],
    photos: [],
    notes: 'Class is calm but chatty. Keep them on task with timed activities.',
    teacherId: 'demo-staff-1',
    teacherEmail: DEMO_EMAILS.teacher(1),
    publishedAt: Date.now() - 1000 * 60 * 60 * 3,
  };
}

/**
 * The lessons the demo COVER teacher walks into. Three, not one.
 *
 * Separate from `buildSeedLesson`, which stays `awaiting_approval` with no
 * classId because the admin dashboard's approvals queue depends on it. A cover
 * teacher needs the opposite: approved, on today, and tied to a real class,
 * because `classId` is what makes the "Know your class" briefing appear.
 *
 * Deliberately NOT maths. The cover TOUR injects its own Year 9 Mathematics
 * sample (COVER_TUTORIAL_SAMPLE_LESSON) only while the tour is running, and a
 * second maths lesson sitting in the demo list made the two indistinguishable.
 *
 * Written as prose, not shorthand. The best case for any of this is a teacher
 * who wrote their notes and then ran Polish over them, so that is what the demo
 * should show: full sentences, no abbreviations, and no subject jargon that a
 * non-specialist cover teacher would have to decode first. "Emphasise the
 * multiply-to-c, add-to-b pair" is notes to self; it is not a brief.
 */
export function buildSeedCoverLessons(school: School): Lesson[] {
  const onDay = (offsetDays: number) => {
    const t = new Date();
    t.setDate(t.getDate() + offsetDays);
    return t.toISOString().split('T')[0];
  };
  const base = {
    status: 'approved' as const,
    // Empty, exactly as createEmptyLesson leaves it. `objectives` and `unit`
    // are legacy fields kept on the type so pre-Phase-2e lessons in Cosmos do
    // not lose content; no door writes either, so a lesson built today has
    // neither. Populating them here would show a demo audience something the
    // product cannot actually produce.
    objectives: [] as string[],
    resources: [],
    photos: [],
    teacherId: 'demo-staff-1',
    teacherEmail: DEMO_EMAILS.teacher(1),
    coverDoor: 'planned' as const,
  };
  const first = school.staff[0];
  const second = school.staff[1];
  const marcus = [first?.firstName ?? 'Marcus', first?.lastName ?? 'Allen'].join(' ');
  const priya = [second?.firstName ?? 'Priya', second?.lastName ?? 'Nair'].join(' ');

  return [
    {
      ...base,
      id: 'demo-lesson-cover-1',
      createdAt: Date.now() - 1000 * 60 * 60 * 20,
      publishedAt: Date.now() - 1000 * 60 * 60 * 18,
      dateISO: onDay(0),
      classId: 'demo-class-2',
      classroom: 'S1',
      subject: 'Science',
      yearGroup: 'Year 8',
      periodNumber: '2',
      periodTime: '09:30 - 10:30',
      coveringFor: marcus,
      lessonAim: 'The class will revise how particles are arranged in solids, liquids and gases, and use that to explain why a gas can be squashed and a solid cannot.',
      // Built through the PLANNED door, so the blocks carry their real kinds:
      // a retrieval starter, middle blocks, and an exit ticket to close. Answer
      // keys sit on the flip side of the blocks that need one, and early
      // finishers is a per-block note rather than the retired standalone block.
      steps: [
        { id: 'c1-s1', kind: 'retrieval', minutes: 10, title: 'Recap: states of matter', detail: 'Already on the board as they come in. They copy the three boxes into their lab books and draw the particles in a solid, a liquid and a gas. They have done this before, so it should be quiet work.', photo: { name: BOARD_DO_NOW_IMAGE_NAME, dataUrl: BOARD_DO_NOW_IMAGE_URL }, answerKey: { detail: 'Solid: touching, in a fixed regular pattern, vibrating on the spot. Liquid: touching but disordered, able to slide past each other. Gas: far apart, disordered, moving quickly in all directions.' } },
        { id: 'c1-s2', kind: 'discussion', minutes: 20, title: 'Explain it back', detail: 'Go through the three diagrams on slide 4 together and ask them to explain each one back to you in their own words. The idea worth returning to is that the particles themselves never change, only how far apart they are and how freely they move.' },
        { id: 'c1-s3', kind: 'worksheet', minutes: 20, title: 'Particle model worksheet', detail: 'Questions 1 to 9. The first five are recall and they should manage those on their own. The last four ask them to explain their reasoning, so expect to prompt.', attachment: { name: 'Y8-particle-model-worksheet.pdf', type: 'application/pdf', size: 184320, dataUrl: 'data:application/pdf;base64,JVBERi0xLjQKJcOkw7zDtsOfCjIgMCBvYmoKPDwvTGVuZ3RoIDMgMCBSL0ZpbHRlci9GbGF0ZURlY29kZT4+CnN0cmVhbQp4nDPQM1Qo5ypUMABCU0MjBV1DAyMFQwsjBV0jY4WQNANDPQNjPUMLPUMTIwVjMwVDCwVDU4WQNAVdIwsFI3MjPUNTUwVDIz0jc3NThZA0BQNzPQNTPUNzIwVDS4WQNAVDCz0jcwsFAyNjhZA0AwMFI3NDQwVdQxNDBUMLC7AiE2NTBSMzBUMLBUMzBUNjBQMLA31DAz1DCz1DEyMFYzMFQwsFQ1MFQzMFA2MFAwsDfUMDfUMLAA==' }, answerKey: { detail: '1 solid, 2 gas, 3 liquid, 4 gas, 5 solid. 6: because the particles are already touching and cannot be pushed closer. 7: because there is space between the particles for them to be pushed into. 8: the particles move faster and spread further apart. 9: no, the particles themselves stay exactly the same size.' }, earlyFinishers: 'Question 13 on the back asks them to explain why a balloon shrinks in the fridge. There are also three challenge cards in the tray on the front bench.' },
        { id: 'c1-s4', kind: 'exit_ticket', minutes: 10, title: 'Exit ticket', detail: 'One sentence explaining why you can squash the air in a bike pump but not the water in a bottle. On a slip of paper with their name on it, collected as they leave.', answerKey: { detail: 'Air is a gas, so there is space between the particles to squash them into. Water is a liquid, so the particles are already touching.' } },
      ],
      resourcePaths: [],
      notes: 'A lively class who settle once the do-now is underway, so get them straight into it. No goggles needed today because there is no practical work.',
    },
    {
      ...base,
      id: 'demo-lesson-cover-2',
      createdAt: Date.now() - 1000 * 60 * 60 * 26,
      publishedAt: Date.now() - 1000 * 60 * 60 * 24,
      dateISO: onDay(0),
      classId: 'demo-class-cov-en',
      classroom: 'E2',
      subject: 'English',
      yearGroup: 'Year 9',
      periodNumber: '4',
      periodTime: '11:40 - 12:40',
      coveringFor: priya,
      lessonAim: 'The class will read chapter three aloud together and begin collecting evidence of how the writer presents loneliness.',
      // Planned door as well, built around a reading block rather than practice.
      steps: [
        { id: 'c2-s1', kind: 'retrieval', minutes: 5, title: 'Recap: where we got to', detail: 'Three quick questions on the board: who has just arrived at the ranch, who does Candy have with him, and what is Curley like? They should manage all three without the book.', answerKey: { detail: 'Curley and his wife have arrived. Candy has his old dog. Curley is aggressive and looking for a fight, particularly with bigger men.' } },
        { id: 'c2-s2', kind: 'reading', minutes: 25, title: 'Reading: chapter three', detail: 'Books are in the cupboard by the window, one between two. They know which page they reached and will tell you. Read around the class, a paragraph each, and take a turn yourself when a passage looks difficult. Do not press anyone who would rather not read aloud; let them pass and move on.' },
        { id: 'c2-s3', kind: 'practice', minutes: 20, title: 'Evidence table', detail: 'They copy the two-column table from slide 2 into their books, the quotation on the left and what it shows on the right. Two or three good lines is plenty. How well they explain each one matters more than how many they find.', link: { url: 'https://example.com/y9-of-mice-and-men-evidence-table.docx', label: 'Evidence table, if they need a printed copy' }, answerKey: { detail: 'Any reasonable choice is fine. The strongest are Candy talking about being left on the ranch when he is no use, Crooks being kept out of the bunkhouse, and Curley\'s wife saying she could have been in pictures. What matters is the explanation, not which line they pick.' }, earlyFinishers: 'Ask them to find a fourth quotation from an earlier chapter and add it to the table, or to write a sentence on which character they think is loneliest and why.' },
        { id: 'c2-s4', kind: 'discussion', minutes: 10, title: 'Share and close', detail: 'Take three or four examples from volunteers and write them on the board, so everyone leaves with more than they found on their own.' },
      ],
      resourcePaths: ['Shared drive: English / Year 9 / Of Mice and Men / Ch3 evidence table.docx'],
      notes: 'The reading aloud is the part they enjoy, so protect the time for it. If you run short, drop the sharing at the end rather than cutting the reading.',
    },
    {
      ...base,
      id: 'demo-lesson-cover-3',
      createdAt: Date.now() - 1000 * 60 * 60 * 30,
      publishedAt: Date.now() - 1000 * 60 * 60 * 28,
      dateISO: onDay(1),
      classId: 'demo-class-cov-geo',
      classroom: 'G1',
      subject: 'Geography',
      yearGroup: 'Year 8',
      periodNumber: '1',
      periodTime: '08:40 - 09:40',
      coveringFor: priya,
      coverDoor: 'emergency',
      lessonAim: 'The class will label the course of a river from source to mouth and describe how the river changes along the way.',
      // The EMERGENCY door, on purpose, so the cover teacher sees both shapes in
      // one week. That door writes two cover_note blocks with fixed synthetic
      // ids and puts the rest on lessonAim, todaySpecifics and notes, so this is
      // exactly what it produces: no block structure and no answer key, because
      // it was written from home before 8am.
      steps: [
        { id: 'emergency-materials', kind: 'cover_note', minutes: 0, title: 'Where to find it', detail: 'The blank river diagrams are printed and in the tray on the front desk. Slides 3 and 4 are on the board already, just press start.' },
        { id: 'emergency-handback', kind: 'cover_note', minutes: 0, title: 'What to do with finished work', detail: 'Diagrams into the tray by the door on their way out. No marking needed, I will go through them tomorrow.' },
      ],
      todaySpecifics: 'They have assembly straight after, so let them go a couple of minutes early rather than holding them.',
      // What the emergency door's materials question produces: a photo taken
      // at home and a link, both hanging off the lesson rather than a block.
      attachments: [
        { id: 'demo-att-geo-1', name: RIVER_DIAGRAM_IMAGE_NAME, type: 'image/png', size: RIVER_DIAGRAM_IMAGE_BYTES, dataUrl: RIVER_DIAGRAM_IMAGE_URL },
      ],
      resourcePaths: [],
      notes: 'First period, so they arrive in ones and twos. The labelling starter works well because they can begin the moment they sit down without waiting for everyone.',
    },
  ];
}

export function buildSeedFeedback(): Feedback {
  return {
    id: 'demo-feedback-1',
    lessonId: 'demo-lesson-2-historical',
    subject: 'Science',
    yearGroup: 'Year 8',
    ratings: [
      { category: 'Behaviour', score: 4, comment: 'Mostly on task, small group needed redirection.' },
      { category: 'Engagement', score: 5 },
      { category: 'Clarity of plan', score: 5, comment: 'Steps and timings were spot on.' },
    ],
    createdAt: Date.now() - 1000 * 60 * 60 * 24 * 2,
  };
}

// ── DEMO-ONLY: retention-review scale data ────────────────────────────
// Generates extra classes each carrying N archived cover observations, so the
// bulk retention-review screen can be viewed at a realistic scale (~100 across
// many classes). Remove this block + the RETENTION_SCALE_CLASSES spread below
// (and the archived arrays on demo-class-1/2) to return the demo to normal.
const ARCH_TEXT = {
  mis: [
    'Kept squaring negatives wrong, treated -3² as 9.',
    'Read the discriminant backwards, thought b²−4ac < 0 gave two real roots.',
    'Confused mass and weight through the whole unit.',
    'Mixed up mean and median under time pressure.',
    'Thought a larger denominator always meant a larger fraction.',
    'Applied BIDMAS strictly left-to-right, ignoring brackets.',
    'Treated "of" as addition rather than multiplication.',
    'Assumed area and perimeter scale the same way.',
    'Muddled independent and dependent variables in the write-up.',
    'Thought heavier objects fall faster in free-fall.',
  ],
  works: [
    'Mini-whiteboards for quick checks kept the pace up.',
    'A short practical demo before the theory settled them.',
    '60 seconds of paired talk before cold-calling raised the hit rate.',
    'Modelling live on the visualiser beat pre-made slides.',
    'A silent do-now on entry set the tone every lesson.',
    'Numbered steps on the board reduced the "I\'m stuck" hands.',
    'Ending with a 3-question exit ticket flagged who to reteach.',
  ],
  can: [
    'Solid on expanding single brackets coming up from Year 8.',
    'Confident reading scales and axes.',
    'Times tables to 12 secure. No calculator needed.',
    'Good at spotting the odd-one-out in a sequence.',
    'Can already balance simple symbol equations.',
  ],
  notYet: [
    'Not secure rearranging formulae yet.',
    'Still shaky converting between fractions and decimals.',
    'Needs more practice with negative indices.',
    'Not yet fluent with standard form.',
  ],
};

function archEntries(pool: string[], count: number, prefix: string, now: number, firstIsRetrieval = false): PillarEntry[] {
  const out: PillarEntry[] = [];
  for (let i = 0; i < count; i++) {
    const wrap = Math.floor(i / pool.length);
    const text = pool[i % pool.length] + (wrap > 0 ? ` (recurring, group ${wrap + 1})` : '');
    const entry: PillarEntry = {
      id: `${prefix}-${i}`,
      text,
      createdAt: now - 1000 * 60 * 60 * 24 * (352 - (i % 10)),
      source: i % 3 === 0 ? 'teacher' : 'cover_observation',
      confirmCount: 1 + (i % 4),
      archivedAt: now - 1000 * 60 * 60 * 24 * (340 - (i % 6)),
      cohortId: 'demo-cohort-prev',
    };
    if (entry.source === 'cover_observation') entry.attributedTo = ['Example teacher', 'Mr Osei', 'Mr Hale', 'Ms Reyes'][i % 4];
    if (firstIsRetrieval && i === 0) { entry.retrievalRequestedAt = now - 1000 * 60 * 60 * 24 * 2; entry.retrievalRequestedBy = entry.attributedTo ?? 'Ms Reyes'; }
    out.push(entry);
  }
  return out;
}

/** Build a demo class holding `count` archived observations spread across the
 *  four archived pillar arrays (≈40% misconceptions, 30% what-works, 20% can,
 *  10% not-yet). DEMO-ONLY — see the note above. */
function buildArchivedClass(
  spec: {
    id: string; label: string; subject: string; year: string; set: string; room: string; teacher: string; count: number; retrieval?: boolean;
    /** This year's profile. Without it the class carries only last year's
     *  archive and a rollover placeholder where the current unit should be,
     *  which is what a thin, neglected class looks like, not a real one two
     *  weeks into September. Live entries are prepended to the archived ones
     *  so the archived counts the retention tour depends on do not change. */
    live?: Partial<ClassContinuityPillars>;
    /** Archived text in the subject's own voice. The shared pool is maths and
     *  physics, and "treated -3² as 9" on an English class reads as what it is. */
    arch?: typeof ARCH_TEXT;
  },
  now: number,
): ClassEntity {
  const { count } = spec;
  const pool = spec.arch ?? ARCH_TEXT;
  const nMis = Math.round(count * 0.4);
  const nWorks = Math.round(count * 0.3);
  const nCan = Math.round(count * 0.2);
  const nNot = Math.max(0, count - nMis - nWorks - nCan);
  return {
    id: spec.id,
    schoolId: 'demo-school-id',
    type: 'class',
    displayLabel: spec.label,
    yearGroup: spec.year,
    subject: spec.subject,
    setIdentifier: spec.set,
    shape: 'secondary',
    schedule: [{ weekIndex: 0, dayOfWeek: 1, periodNumber: '1' }],
    defaultRoom: spec.room,
    regularTeacherIds: ['demo-staff-1'],
    academicYearStarted: '2026-09-01',
    continuityPillars: {
      currentUnit: 'Cohort rolled over, last year\'s observations archived',
      pacing: 'on_track',
      ...spec.live,
      knownMisconceptions: [...(spec.live?.knownMisconceptions ?? []), ...archEntries(pool.mis, nMis, `${spec.id}-mis`, now, spec.retrieval)],
      whatWorksWithThem: [...(spec.live?.whatWorksWithThem ?? []), ...archEntries(pool.works, nWorks, `${spec.id}-works`, now)],
      priorKnowledge: {
        canAlready: [...(spec.live?.priorKnowledge?.canAlready ?? []), ...archEntries(pool.can, nCan, `${spec.id}-can`, now)],
        notMetYet: [...(spec.live?.priorKnowledge?.notMetYet ?? []), ...archEntries(pool.notYet, nNot, `${spec.id}-not`, now)],
      },
    },
    status: 'active',
    createdAt: now - 1000 * 60 * 60 * 24 * 30,
    updatedAt: now - 1000 * 60 * 60 * 24 * 30,
    createdBy: 'demo-school-id',
  };
}

/**
 * Set true to seed the retention review at realistic scale (~94 archived
 * observations across 7 classes). Off by default: the guided tours walk the
 * retention screen, and 94 notes across 7 classes is unreadable on a phone and
 * unusable as a teaching example. Left in place rather than deleted because it
 * is the only thing that shows the screen holds up at scale.
 */
const DEMO_RETENTION_SCALE = false;

const RETENTION_SCALE_SPECS: Parameters<typeof buildArchivedClass>[0][] = [
  { id: 'demo-class-r1', label: 'English Set 3', subject: 'English', year: 'Year 10', set: 'Set 3', room: 'E4', teacher: 'demo-staff-1', count: 30, retrieval: true },
  { id: 'demo-class-r2', label: 'History GCSE', subject: 'History', year: 'Year 11', set: 'Set 1', room: 'H2', teacher: 'demo-staff-1', count: 20 },
  { id: 'demo-class-r3', label: 'Geography Set 2', subject: 'Geography', year: 'Year 9', set: 'Set 2', room: 'G1', teacher: 'demo-staff-2', count: 15, retrieval: true },
  { id: 'demo-class-r4', label: 'French Set 1', subject: 'French', year: 'Year 8', set: 'Set 1', room: 'L3', teacher: 'demo-staff-2', count: 12 },
  { id: 'demo-class-r5', label: 'Physics Set 1', subject: 'Physics', year: 'Year 11', set: 'Set 1', room: 'S3', teacher: 'demo-staff-1', count: 8 },
  { id: 'demo-class-r6', label: 'Art & Design', subject: 'Art', year: 'Year 9', set: 'Mixed', room: 'A1', teacher: 'demo-staff-2', count: 5 },
  { id: 'demo-class-r7', label: 'Computing Set 2', subject: 'Computing', year: 'Year 10', set: 'Set 2', room: 'C2', teacher: 'demo-staff-1', count: 4 },
];

// Two weeks into September 2026, as a real teacher would have written them.
// Positions, never names. Live entries sit alongside last year's archived ones,
// so the classes read as classes rather than as retention fixtures.
const daysAgo = (n: number) => Date.now() - 1000 * 60 * 60 * 24 * n;
const live = (id: string, text: string, days: number, source: PillarEntry['source'] = 'teacher', attributedTo?: string): PillarEntry => ({
  id, text, createdAt: daysAgo(days), source, confirmCount: source === 'teacher' ? 1 : 2, cohortId: 'demo-cohort-current',
  ...(attributedTo ? { attributedTo } : {}),
});

const TOUR_LIVE = {
  english9: {
    currentUnit: 'Gothic fiction: openings and atmosphere. Two lessons in, first piece of extended writing due Friday',
    pacing: 'on_track',
    behaviourNorm: 'Warm and talkative, and the talk is mostly about the work once they start. Two pupils on the window side settle faster apart than together, which the seating plan already does.',
    escalationRoute: 'Three-step warning system. The Head of English is in E1 next door and takes anyone who needs a change of room.',
    entryRoutine: 'Books out, date and title written, the sentence on the board copied and improved before anyone speaks',
    exitRoutine: 'Books left open at today\'s work on the front desk so it can be marked, chairs under',
    workingNoise: 'quiet_discussion',
    workingNoiseNote: 'silent for the opening sentence, then paired talk about the extract is fine',
    messageFromTeacher: 'Morning 9E4. The extract is the one we started on Tuesday, so nobody has not seen it. Read it aloud yourself first; it lands better than asking them to.',
    pedagogyNorm: 'Read aloud, then talk, then write. They write far more after two minutes of talking about it.',
    equipment: ['exercise_books', 'physical_worksheets'],
    otherDistinctives: 'One pupil on the front-right table reads well below the class and has a printed copy of every extract in the blue folder on the desk. Two pupils on the back table speak Polish at home and are fluent; they prefer to check a word with each other first.',
    knownMisconceptions: [live('demo-cls3-mis-live-1', 'They think atmosphere means the weather in the scene. Push them to the sounds and the small details instead.', 9)],
    whatWorksWithThem: [live('demo-cls3-works-live-1', 'Giving them one sentence to improve, rather than a paragraph to write, gets the whole class started in under a minute.', 4, 'cover_observation', 'Example teacher')],
    priorKnowledge: {
      canAlready: [live('demo-cls3-can-live-1', 'They can identify a simile and a metaphor reliably. The gap is saying what effect it has.', 12)],
      notMetYet: [],
    },
  } satisfies Partial<ClassContinuityPillars>,
  history11: {
    examBoard: 'AQA',
    specCode: '8145',
    currentUnit: 'Elizabethan England: religion and the settlement of 1559. Source questions this fortnight',
    pacing: 'on_track',
    behaviourNorm: 'Small GCSE group, serious and quiet. The risk is not noise, it is drift: a pair at the back will talk about anything but history if the task is loose.',
    escalationRoute: 'Three-step warning system. The Head of Humanities is in H1. Nobody has needed it with this group.',
    entryRoutine: 'Knowledge quiz from the board in silence, ten questions, self-marked',
    exitRoutine: 'Revision guides back on the shelf by the door, quiz scores read out to the register',
    workingNoise: 'silent',
    workingNoiseNote: 'silent for the quiz and any source work, discussion only when asked for',
    messageFromTeacher: 'Morning 11H. The source pack is on my desk, one between two. Do the quiz first even if it feels like a slow start; it is the only retrieval they get.',
    pedagogyNorm: 'Retrieval first, every lesson. Then one source worked slowly together before they try one alone.',
    equipment: ['exercise_books', 'textbook'],
    otherDistinctives: 'One pupil at the front has extra time in exams and will take longer on the quiz; do not call time on them. Two pupils on the right have a scribe arrangement for extended writing only.',
    knownMisconceptions: [live('demo-cls4-mis-live-1', 'They write "the Church" as if it were one thing. Catholic and Protestant have to be named every time.', 8)],
    whatWorksWithThem: [live('demo-cls4-works-live-1', 'Reading the source aloud before they look at the question stops them answering a question they have not read.', 5, 'cover_observation', 'Example teacher')],
    priorKnowledge: {
      canAlready: [live('demo-cls4-can-live-1', 'They know the 1559 Acts by name and date without prompting.', 13)],
      notMetYet: [],
    },
  } satisfies Partial<ClassContinuityPillars>,
  geography8: {
    currentUnit: 'Weather and climate: air masses, and why Britain gets the weather it does',
    pacing: 'ahead',
    behaviourNorm: 'Keen and quick, and quick means loud once they have finished. Have the extension on the board before the first hand goes up.',
    escalationRoute: 'Three-step warning system. The Geography office is opposite G1 and someone is always in it.',
    entryRoutine: 'Atlases out, the map question from the board attempted before the register',
    exitRoutine: 'Atlases back in the trolley in number order, worksheets into the tray on the way out',
    workingNoise: 'collaborative',
    workingNoiseNote: 'they work in table groups and the noise is fine while it is about the map',
    messageFromTeacher: 'Morning 8G1. They will finish fast. The challenge cards are in the tray on the front bench; hand them out the moment a group is done, not when they ask.',
    pedagogyNorm: 'Map first, then explanation. Ask them to point to it before they describe it.',
    equipment: ['exercise_books', 'physical_worksheets'],
    otherDistinctives: 'One pupil on the left-hand table is colour blind and needs the key read out for any map using red and green. One pupil by the door wears a hearing aid; face the class when you speak.',
    knownMisconceptions: [live('demo-cls5-mis-live-1', 'They think the sea makes the weather warmer in every season. It is the opposite in summer, which surprises them every year.', 10)],
    whatWorksWithThem: [live('demo-cls5-works-live-1', 'Getting one group to explain the air mass to the rest of the class beats you explaining it twice.', 3, 'cover_observation', 'Example teacher')],
    priorKnowledge: {
      canAlready: [live('demo-cls5-can-live-1', 'Compass directions and grid references are secure from Year 7.', 14)],
      notMetYet: [],
    },
  } satisfies Partial<ClassContinuityPillars>,
};

// Last year's archived observations, in each subject's own voice.
const TOUR_ARCH: Record<'english9' | 'history11' | 'geography8', typeof ARCH_TEXT> = {
  english9: {
    mis: ['Kept writing speech without a new line for each speaker.'],
    works: ['Sentence starters on the board doubled what the quieter half wrote.'],
    can: ['Could structure a point-evidence-explain paragraph without a scaffold by the spring term.'],
    notYet: ['Embedding quotations still needed prompting.'],
  },
  history11: {
    mis: ['Called every source biased without reading it first.'],
    works: ['Timing each exam question against the clock on the board kept them honest about pace.'],
    can: ['Knew the order of the Tudor monarchs cold.'],
    notYet: ['Could not yet weigh two sources against each other in one paragraph.'],
  },
  geography8: {
    mis: ['Used weather and climate as if they meant the same thing all autumn.'],
    works: ['Colouring the map before labelling it kept the labels in the right places.'],
    can: ['Four-figure grid references were secure by half term.'],
    notYet: ['Six-figure grid references were not yet reliable.'],
  },
};

/**
 * The tour-sized retention fixture. Counts are deliberately tiny: the retention
 * steps ask the user to read observations and decide, which is only possible
 * with a handful. demo-class-3 carries a retrieval request and is a DIFFERENT
 * class from the one the teacher tour opens, because the admin narrative sends
 * them to a colleague's class, not the one they are already looking at.
 */
const TOUR_RETENTION_SPECS: Parameters<typeof buildArchivedClass>[0][] = [
  // Set 4 and Set 1, not Set 2 and Set 3: the cover demo's two classes are
  // "English Set 2, Year 9" and "Geography Set 3, Year 8", and a school with two
  // classes of the same name in the same year reads as a bug on the Classes page.
  { id: 'demo-class-3', label: 'English Set 4', subject: 'English', year: 'Year 9', set: 'Set 4', room: 'E2', teacher: 'demo-staff-2', count: 3, retrieval: true, live: TOUR_LIVE.english9, arch: TOUR_ARCH.english9 },
  { id: 'demo-class-4', label: 'History GCSE', subject: 'History', year: 'Year 11', set: 'Set 1', room: 'H2', teacher: 'demo-staff-1', count: 2, live: TOUR_LIVE.history11, arch: TOUR_ARCH.history11 },
  { id: 'demo-class-5', label: 'Geography Set 1', subject: 'Geography', year: 'Year 8', set: 'Set 1', room: 'G1', teacher: 'demo-staff-2', count: 2, retrieval: true, live: TOUR_LIVE.geography8, arch: TOUR_ARCH.geography8 },
];

/** Pre-seeded Class entities for the demo school admin — gives the new
 *  Classes tab something to show without forcing the demo user through
 *  authoring. Two secondary classes (one per seeded teacher) plus an
 *  ad-hoc special-day class so all three shapes are visible. */
export function buildSeedClasses(): ClassEntity[] {
  const now = Date.now();
  const academicYearStart = '2026-09-01';
  return [
    {
      id: 'demo-class-1',
      schoolId: 'demo-school-id',
      type: 'class',
      displayLabel: 'Mathematics Set 1',
      yearGroup: 'Year 9',
      subject: 'Mathematics',
      setIdentifier: 'Set 1',
      shape: 'secondary',
      schedule: [
        { weekIndex: 0, dayOfWeek: 2, periodNumber: '2' },
        { weekIndex: 0, dayOfWeek: 4, periodNumber: '4' },
      ],
      defaultRoom: 'M1',
      regularTeacherIds: ['demo-staff-1'],
      academicYearStarted: academicYearStart,
      // Phase 2d.1 — fully-populated demo continuity profile so the
      // class picker + admin Classes view shows what a "good" profile
      // looks like in the wild.
      continuityPillars: {
        examBoard: 'AQA',
        specCode: '8300',
        tier: 'higher',
        currentUnit: 'Quadratics, starting the formula tomorrow',
        pacing: 'on_track',
        behaviourNorm: 'Top set and engaged. Two pupils on the back-left table need separating during paired work, they are marked on the seating plan.',
        entryRoutine: 'Silent do-now from the board',
        pedagogyNorm: 'Mastery. We don\'t move on until everyone is solid',
        equipment: ['textbook', 'exercise_books', 'calculators', 'whiteboards'],
        calculatorPolicy: 'topic_dependent',
        escalationRoute: 'Three-step warning system. The Head of Maths is in M2 and will take anyone who needs removing, which has not happened this year.',
        exitRoutine: 'Do-now sheets into the tray, calculators back in the box by number, books away',
        workingNoise: 'quiet_discussion',
        workingNoiseNote: 'silent for the do-now and any test, otherwise talking about the maths is fine',
        messageFromTeacher: 'Morning 9M1. Do the do-now in silence, then the worksheet in pairs. The answers are on slide 3; do not put them up until the last five minutes.',
        otherDistinctives: 'Two pupils on the back-left table are marked on the seating plan and work better apart. One pupil on the front row uses a laptop for all written work; that is agreed and not for debate.',
        // DEMO-ONLY: archived cover observations so the retention review screen
        // + amber "review is open" banner have something to show. Remove to
        // return the demo to its normal (nothing-to-review) state.
        knownMisconceptions: [
          // This year's, so the misconceptions panel shows a live note beside
          // the archived ones rather than nothing at all.
          { id: 'demo-cls1-mis-live-1', text: 'They read 3x squared as (3x) squared. Worth writing both on the board and asking which one they mean.', createdAt: now - 1000 * 60 * 60 * 24 * 11, source: 'teacher', confirmCount: 1, cohortId: 'demo-cohort-current' },
          { id: 'm-arc-1', text: 'Kept squaring negatives wrong, treated -3² as 9. Needed the "square, then negate" reminder most lessons.', createdAt: now - 1000 * 60 * 60 * 24 * 350, source: 'cover_observation', attributedTo: 'Example teacher', confirmCount: 3, archivedAt: now - 1000 * 60 * 60 * 24 * 340, cohortId: 'demo-cohort-prev' },
          { id: 'm-arc-2', text: 'Read the discriminant backwards, thought b²−4ac < 0 gave two real roots.', createdAt: now - 1000 * 60 * 60 * 24 * 345, source: 'cover_observation', attributedTo: 'Mr Osei', confirmCount: 2, archivedAt: now - 1000 * 60 * 60 * 24 * 340, retrievalRequestedAt: now - 1000 * 60 * 60 * 24 * 2, retrievalRequestedBy: 'Mr Osei', cohortId: 'demo-cohort-prev' },
        ],
        whatWorksWithThem: [
          // LIVE (not archived) and authored by the teacher — the anchor for the
          // teacher tour's "what you already know" step. Every other entry on
          // this class is archived, and PillarObservations drops any group whose
          // entries are all archived, so without this the panel renders nothing
          // and that step would have had no target at all.
          { id: 'demo-note-teacher-1', text: 'Starts strong but fades after ~20 min, front-load the hard thinking and put practice at the end.', createdAt: now - 1000 * 60 * 60 * 24 * 21, source: 'teacher', confirmCount: 1, cohortId: 'demo-cohort-current' },
          { id: 'm-arc-3', text: 'Mini-whiteboards for quick checks kept the pace up and caught errors early.', createdAt: now - 1000 * 60 * 60 * 24 * 348, source: 'cover_observation', attributedTo: 'Example teacher', confirmCount: 4, archivedAt: now - 1000 * 60 * 60 * 24 * 340, cohortId: 'demo-cohort-prev' },
        ],
        priorKnowledge: {
          canAlready: [
            { id: 'demo-cls1-can-live-1', text: 'Expanding double brackets is secure. Factorising is what this half term is building.', createdAt: now - 1000 * 60 * 60 * 24 * 16, source: 'teacher', confirmCount: 1, cohortId: 'demo-cohort-current' },
            { id: 'm-arc-4', text: 'Solid on expanding double brackets coming up from Year 8.', createdAt: now - 1000 * 60 * 60 * 24 * 352, source: 'teacher', confirmCount: 1, archivedAt: now - 1000 * 60 * 60 * 24 * 340, cohortId: 'demo-cohort-prev' },
          ],
          notMetYet: [],
        },
      },
      status: 'active',
      createdAt: now - 1000 * 60 * 60 * 24 * 30,
      updatedAt: now - 1000 * 60 * 60 * 24 * 30,
      createdBy: 'demo-school-id',
    },
    {
      id: 'demo-class-2',
      schoolId: 'demo-school-id',
      type: 'class',
      displayLabel: 'Science Set 2',
      yearGroup: 'Year 8',
      subject: 'Science',
      setIdentifier: 'Set 2',
      shape: 'secondary',
      schedule: [
        { weekIndex: 0, dayOfWeek: 1, periodNumber: '3' },
        { weekIndex: 0, dayOfWeek: 3, periodNumber: '5' },
        { weekIndex: 0, dayOfWeek: 5, periodNumber: '1' },
      ],
      defaultRoom: 'S1',
      regularTeacherIds: ['demo-staff-2'],
      academicYearStarted: academicYearStart,
      continuityPillars: {
        currentUnit: 'Particle model of matter, moving to density next week',
        pacing: 'on_track',
        messageFromTeacher:
          'Morning 8D. No practical today, so goggles stay in the drawer. Do the do-now in silence please, then you can talk while you work. Your bench monitors will hand out the worksheets.',
        entryRoutine: 'Bags under the bench, lab book open at the do-now, standing behind stools until told to sit',
        exitRoutine: 'Stools tucked under, benches clear, worksheets left on the front bench',
        workingNoise: 'quiet_discussion',
        workingNoiseNote: 'silent for the do-now, then talking is fine as long as it is about the work',
        equipment: ['textbook', 'exercise_books', 'physical_worksheets'],
        customEquipment: ['Mini whiteboards, shelf under the front bench'],
        labSafetyBriefing: false,
        behaviourNorm:
          'Lively and eager, and they settle completely once a structured do-now is in front of them. The first two minutes decide the lesson, so have it on the board before they come in.',
        escalationRoute:
          'Three-step warning system. Mr Adeyemi is in S2 next door and will take anyone who needs removing. Do not send anyone to the prep room.',
        pedagogyNorm:
          'Show it, then ask them to explain it back in their own words before they write anything. They can recite definitions without understanding them, and the explaining back is what catches that.',
        otherDistinctives:
          'One pupil on the front bench likes a job and settles instantly if given one, so use them for handing out. Two pupils on the middle-right bench are best kept apart during written work. Two pupils on the back-right bench speak English as an additional language and understand far more than they will say out loud; check in with them quietly part way through a task rather than asking them in front of the class. One pupil has a medical pass and may leave without asking, which is agreed with the school and does not need challenging.',
        priorKnowledge: {
          canAlready: [
            {
              id: 'demo-cls2-can-1',
              text: 'They can name the three states and give an everyday example of each.',
              createdAt: now - 1000 * 60 * 60 * 24 * 28,
              source: 'teacher',
              confirmCount: 3,
              cohortId: 'demo-cohort-current',
            },
            {
              id: 'demo-cls2-can-2',
              text: 'They can draw the particle diagrams from memory. That is not the hard part for this group.',
              createdAt: now - 1000 * 60 * 60 * 24 * 21,
              source: 'teacher',
              confirmCount: 2,
              cohortId: 'demo-cohort-current',
            },
          ],
          notMetYet: [
            {
              id: 'demo-cls2-not-1',
              text: 'Density has not been taught yet. If it comes up, park it rather than explaining it early.',
              createdAt: now - 1000 * 60 * 60 * 24 * 10,
              source: 'teacher',
              confirmCount: 1,
              cohortId: 'demo-cohort-current',
            },
          ],
        },
        whatWorksWithThem: [
          {
            id: 'demo-cls2-works-1',
            text: 'Mini whiteboards for a quick check. You see every answer at once and they enjoy holding them up, so nobody hides.',
            createdAt: now - 1000 * 60 * 60 * 24 * 19,
            source: 'cover_observation',
            attributedTo: 'Example teacher',
            confirmCount: 4,
            cohortId: 'demo-cohort-current',
          },
          {
            id: 'demo-cls2-works-2',
            text: 'Giving the bike pump and the water bottle as the example lands the compression idea faster than any diagram.',
            createdAt: now - 1000 * 60 * 60 * 24 * 7,
            source: 'cover_observation',
            attributedTo: 'Example teacher',
            confirmCount: 2,
            cohortId: 'demo-cohort-current',
          },
        ],
        knownMisconceptions: [
          {
            id: 'demo-cls2-mis-1',
            text: 'Nearly all of them think the particles themselves expand when something is heated, rather than moving further apart. Worth saying out loud even if nobody asks.',
            createdAt: now - 1000 * 60 * 60 * 24 * 24,
            source: 'cover_observation',
            attributedTo: 'Example teacher',
            confirmCount: 5,
            cohortId: 'demo-cohort-current',
          },
          {
            id: 'demo-cls2-mis-2',
            text: 'They use particle, atom and molecule interchangeably. Insist on particle today; the distinction comes later in the year.',
            createdAt: now - 1000 * 60 * 60 * 24 * 15,
            source: 'teacher',
            confirmCount: 3,
            cohortId: 'demo-cohort-current',
          },
        ],
      },
      status: 'active',
      createdAt: now - 1000 * 60 * 60 * 24 * 30,
      updatedAt: now - 1000 * 60 * 60 * 24 * 30,
      createdBy: 'demo-school-id',
    },
    // Small, readable set for the guided tours: three classes with a handful of
    // archived observations each, two of them carrying an open retrieval
    // request so both the teacher's "ask for it back" and the admin's "restore
    // it" halves of the loop have something real to act on.
    // Two classes for the cover demo's other two lessons.
    //
    // APPENDED rather than reusing the retention fixtures, because those carry
    // deliberately thin pillars (a rollover placeholder and archived entries)
    // and a cover briefing built on them reads as empty. Appending is safe on
    // both counts that matter: the retention walk pools only classes where
    // countArchivedEntries > 0, so classes with nothing archived cannot change
    // its "class 1 of N", and demo-class-1 stays first in the list, so the
    // admin Classes tour still opens on the class it was written for.
    {
      id: 'demo-class-cov-en',
      schoolId: 'demo-school-id',
      type: 'class',
      displayLabel: 'English Set 2',
      yearGroup: 'Year 9',
      subject: 'English',
      setIdentifier: 'Set 2',
      shape: 'secondary',
      schedule: [{ weekIndex: 0, dayOfWeek: 4, periodNumber: '4' }],
      defaultRoom: 'E2',
      regularTeacherIds: ['demo-staff-2'],
      academicYearStarted: academicYearStart,
      continuityPillars: {
        examBoard: 'AQA',
        specCode: '8702',
        currentUnit: 'Of Mice and Men, part way through chapter three',
        pacing: 'on_track',
        messageFromTeacher:
          'Morning 9B. Carry on exactly as we have been: read together, then write. I want two or three quotations in your books by the end, not ten. Your usual reader for this week can take the long passages.',
        entryRoutine: 'Books out, date and title written, first question attempted before anything is said to them',
        exitRoutine: 'Books stacked on the front desk by the door, chairs under',
        workingNoise: 'quiet_discussion',
        workingNoiseNote: 'silent while anyone is reading aloud, paired talk once they are writing',
        equipment: ['exercise_books', 'physical_worksheets'],
        customEquipment: ['Class set of Of Mice and Men, cupboard by the window'],
        behaviourNorm:
          'Warm and talkative, and they will chat a task away if it has no finish time. Give them a number of minutes and hold to it and they are excellent.',
        escalationRoute:
          'Three-step warning system. If it goes past a second warning, send them to Mr Hale in E4 next door rather than out into the corridor.',
        pedagogyNorm:
          'Read aloud together first, then write. They lose the thread entirely if asked to read silently, and the writing gets much thinner.',
        otherDistinctives:
          'Two pupils on the front-right table read aloud confidently and will volunteer every single time. Spread the turns or the same two voices carry the whole lesson. One pupil on the left-hand row uses a reading pen and would rather not read out; let them pass without comment and they will contribute in discussion instead. One pupil joined in October from another school and has not read chapters one and two, so check they are following before the writing task.',
        priorKnowledge: {
          canAlready: [
            {
              id: 'demo-cov-en-can-1',
              text: 'They can find a quotation that fits an idea when you tell them what the idea is.',
              createdAt: now - 1000 * 60 * 60 * 24 * 30,
              source: 'teacher',
              confirmCount: 2,
              cohortId: 'demo-cohort-current',
            },
            {
              id: 'demo-cov-en-can-2',
              text: 'They know the main characters and the setting well enough that you do not need to recap.',
              createdAt: now - 1000 * 60 * 60 * 24 * 22,
              source: 'teacher',
              confirmCount: 1,
              cohortId: 'demo-cohort-current',
            },
          ],
          notMetYet: [
            {
              id: 'demo-cov-en-not-1',
              text: 'They have not been taught how to write about the writer deliberately choosing a word. Do not expect "Steinbeck uses" phrasing yet.',
              createdAt: now - 1000 * 60 * 60 * 24 * 20,
              source: 'teacher',
              confirmCount: 1,
              cohortId: 'demo-cohort-current',
            },
          ],
        },
        whatWorksWithThem: [
          {
            id: 'demo-cov-en-works-1',
            text: 'Give them the quotation first and ask what it shows, rather than asking them to hunt for one. Far more comes out of them that way.',
            createdAt: now - 1000 * 60 * 60 * 24 * 18,
            source: 'teacher',
            confirmCount: 2,
            cohortId: 'demo-cohort-current',
          },
          {
            id: 'demo-cov-en-works-2',
            text: 'A visible countdown on the board for writing time. They work steadily against a clock and drift without one.',
            createdAt: now - 1000 * 60 * 60 * 24 * 9,
            source: 'cover_observation',
            attributedTo: 'Example teacher',
            confirmCount: 3,
            cohortId: 'demo-cohort-current',
          },
        ],
        knownMisconceptions: [
          {
            id: 'demo-cov-en-mis-1',
            text: 'They retell the plot when asked to analyse. Asking "and what does that tell us about him?" pushes them past summary every time.',
            createdAt: now - 1000 * 60 * 60 * 24 * 25,
            source: 'cover_observation',
            attributedTo: 'Example teacher',
            confirmCount: 3,
            cohortId: 'demo-cohort-current',
          },
          {
            id: 'demo-cov-en-mis-2',
            text: 'Several think a long quotation is a better quotation. Short line, long comment is the rule they need reminding of.',
            createdAt: now - 1000 * 60 * 60 * 24 * 14,
            source: 'cover_observation',
            attributedTo: 'Example teacher',
            confirmCount: 2,
            cohortId: 'demo-cohort-current',
          },
        ],
      },
      status: 'active',
      createdAt: now - 1000 * 60 * 60 * 24 * 200,
      updatedAt: now - 1000 * 60 * 60 * 24 * 3,
      createdBy: 'demo',
    },
    {
      id: 'demo-class-cov-geo',
      schoolId: 'demo-school-id',
      type: 'class',
      displayLabel: 'Geography Set 3',
      yearGroup: 'Year 8',
      subject: 'Geography',
      setIdentifier: 'Set 3',
      shape: 'secondary',
      schedule: [{ weekIndex: 0, dayOfWeek: 5, periodNumber: '1' }],
      defaultRoom: 'G1',
      regularTeacherIds: ['demo-staff-2'],
      academicYearStarted: academicYearStart,
      continuityPillars: {
        currentUnit: 'Rivers, starting the course from source to mouth',
        pacing: 'behind',
        messageFromTeacher:
          'Morning 8C. We are a lesson behind so please do get through the labelling today. Start the moment you have four or five of them in the room, do not wait for everyone. Your usual monitors will hand out the diagrams.',
        entryRoutine: 'Coats off and on the back of the chair, diagram out, labelling started without being asked',
        exitRoutine: 'Diagrams into the tray by the door on the way out',
        workingNoise: 'quiet_discussion',
        workingNoiseNote: 'they help each other with labels, which is fine and worth allowing',
        equipment: ['exercise_books', 'physical_worksheets'],
        customEquipment: ['Coloured pencils, box on the side bench'],
        behaviourNorm:
          'First period, so they arrive in ones and twos over about five minutes. Nothing that needs the whole class present works well as an opener.',
        escalationRoute:
          'Three-step warning system. Mrs Okafor is in G2 opposite and will take anyone who needs to be out of the room.',
        pedagogyNorm:
          'Diagram and labelling before writing, always. They will write a proper paragraph once they can see the thing, and almost nothing if asked to write first.',
        otherDistinctives:
          'One pupil on the front-left table is reliable and likes a job, so give them the handing out and they settle straight away. Two pupils, one front-right and one back-left, will talk across the room to each other if they end up on the same table; they are seated apart on the plan and it is worth keeping it that way. Four pupils find extended writing hard going, and the sentence starters on slide 4 are the difference between half a line and a paragraph, so point them out rather than waiting to be asked. One pupil on the front row needs you facing the class when you speak rather than the board, so they can lip-read.',
        priorKnowledge: {
          canAlready: [
            {
              id: 'demo-cov-geo-can-1',
              text: 'They can name the source and the mouth and know a river flows downhill.',
              createdAt: now - 1000 * 60 * 60 * 24 * 16,
              source: 'teacher',
              confirmCount: 2,
              cohortId: 'demo-cohort-current',
            },
          ],
          notMetYet: [
            {
              id: 'demo-cov-geo-not-1',
              text: 'Meander and tributary are new words this week. Expect to define both rather than assume them.',
              createdAt: now - 1000 * 60 * 60 * 24 * 8,
              source: 'teacher',
              confirmCount: 1,
              cohortId: 'demo-cohort-current',
            },
          ],
        },
        whatWorksWithThem: [
          {
            id: 'demo-cov-geo-works-1',
            text: 'Labelling from memory first, before being shown the answers, tells you in two minutes what they actually remember.',
            createdAt: now - 1000 * 60 * 60 * 24 * 12,
            source: 'teacher',
            confirmCount: 1,
            cohortId: 'demo-cohort-current',
          },
          {
            id: 'demo-cov-geo-works-2',
            text: 'Asking them to name a river they have actually seen gets the quiet ones talking. Several have been to the Thames on a trip.',
            createdAt: now - 1000 * 60 * 60 * 24 * 6,
            source: 'cover_observation',
            attributedTo: 'Example teacher',
            confirmCount: 2,
            cohortId: 'demo-cohort-current',
          },
        ],
        knownMisconceptions: [
          {
            id: 'demo-cov-geo-mis-1',
            text: 'Most of them think a river is fastest at the mouth because it is widest there. It is the opposite, and it catches nearly all of them.',
            createdAt: now - 1000 * 60 * 60 * 24 * 11,
            source: 'cover_observation',
            attributedTo: 'Example teacher',
            confirmCount: 4,
            cohortId: 'demo-cohort-current',
          },
        ],
      },
      status: 'active',
      createdAt: now - 1000 * 60 * 60 * 24 * 200,
      updatedAt: now - 1000 * 60 * 60 * 24 * 5,
      createdBy: 'demo',
    },
    ...TOUR_RETENTION_SPECS.map((s) => buildArchivedClass(s, now)),
    ...(DEMO_RETENTION_SCALE ? RETENTION_SCALE_SPECS.map((s) => buildArchivedClass(s, now)) : []),
  ];
}

/**
 * One pending cover-teacher suggestion on demo-class-1, so the teacher tour's
 * "a cover teacher noticed something" step has a real decision to make rather
 * than an explanation to read. Deliberately an `append` to knownMisconceptions:
 * that is the kind that actually writes an attributed entry into the profile,
 * so applying it visibly changes something.
 */
export function buildSeedPillarSuggestions(): PillarSuggestion[] {
  const now = Date.now();
  return [
    {
      id: 'demo-suggestion-1',
      type: 'pillar_suggestion',
      schoolId: 'demo-school-id',
      classId: 'demo-class-1',
      lessonId: 'demo-lesson-1',
      coverTeacherName: 'Example teacher',
      coverTeacherEmail: 'bianchi.cover@example.sch.uk',
      field: 'knownMisconceptions',
      kind: 'append',
      suggestedValue: 'Several read 3x² as (3x)², worth a starter on where the power actually sits.',
      status: 'pending',
      createdAt: now - 1000 * 60 * 60 * 26,
    },
  ];
}

/** A tiny seed of pupils so the Pupil entity surface has something to
 *  render later in the redesign. School-level; classMemberships link to
 *  the seeded classes above. */
export function buildSeedPupils(): PupilEntity[] {
  const now = Date.now();
  return [
    {
      id: 'demo-pupil-1',
      schoolId: 'demo-school-id',
      type: 'pupil',
      displayFirstName: 'Marcus',
      fullName: 'Marcus Tate',
      enrolledFrom: '2024-09-01',
      classMemberships: [
        { classId: 'demo-class-1', joinedDate: '2026-09-01' },
      ],
      flags: [],
      status: 'active',
      createdAt: now,
      updatedAt: now,
    },
  ];
}
